import React, { useState, useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { TextField, Box, Button, Checkbox, FormControlLabel, FormControl, FormLabel, Tooltip, LinearProgress } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { useSelector, shallowEqual } from "react-redux";
import { ConfirmButton } from ".";
import { WatchSelectors, WatchActions, useDispatch } from "../../../../state";
import { authPost } from "../../../../lib";
import { MultiSelectChips } from "../MultiSelectChips";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import RemoveCircleOutlineOutlinedIcon from "@material-ui/icons/RemoveCircleOutlineOutlined";
import { useForceUpdate } from "../../../../lib";
import { RecentValuesInput } from "../RecentValuesInput";

interface Props {
  attribute: string;
  path?: string;
  rawAttribute: string;
  brandAffiliated: true;
  isCaseShape: boolean;
  onSuccess?: () => void;
}

const selectStyle: React.CSSProperties = {
  fontSize: "12px",
  height: "24px",
  padding: "2px 12px 2px 5px",
  border: "1px solid rgba(0, 0, 0, 0.23)",
  backgroundColor: "transparent",
  width: "100%",
};

const inputStyle: React.CSSProperties = {
  fontSize: "12px",
  height: "24px",
  padding: "2px 5px",
  border: "1px solid rgba(0, 0, 0, 0.23)",
  backgroundColor: "transparent",
  width: "100%",
  boxSizing: "border-box",
  outline: "none",
  fontFamily: "inherit",
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  display: "block",
  marginBottom: 2,
};

function AddCalibreForm({ onSuccess }: { onSuccess?: () => void }) {
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const siteId = useSelector((state: any) => state.scopes.site_id);
  const { brand: brandList } = useSelector(WatchSelectors.watches, shallowEqual);

  const brandListTitle = brandList?.map((b: any) => ({ title: b.brand, id: b.brand_id })) || [];

  const [calibreComplicationList, setCalibreComplicationList] = useState<any[]>([]);
  const [certificationList, setCertificationList] = useState<any[]>([]);
  const [calibreList, setCalibreList] = useState<any[]>([]);
  const [movementTypeList, setMovementTypeList] = useState<any[]>([]);
  const [listsLoading, setListsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setListsLoading(true);
      const [cert, comp, calibres, movTypes] = await Promise.all([
        dispatch(WatchActions.getAttribute("certification", null, siteId)),
        dispatch(WatchActions.getAttribute("calibre_complication", null, siteId)),
        WatchActions.getCalibreList(),
        WatchActions.getMovementTypeList(),
      ]);
      setCertificationList((cert as any) || []);
      setCalibreComplicationList((comp as any) || []);
      setCalibreList(calibres || []);
      setMovementTypeList(movTypes || []);
      setListsLoading(false);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  const [calibreName, setCalibreName] = useState("");
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null);
  const [baseCalibreId, setBaseCalibreId] = useState<number | "">("");
  const [movementTypeId, setMovementTypeId] = useState<number | "">("");
  const [inHouse, setInHouse] = useState(false);
  const [hz, setHz] = useState<any>("");
  const [vph, setVph] = useState<any>("");
  const [hzError, setHzError] = useState(false);
  const [jewels, setJewels] = useState<any>("");
  const [components, setComponents] = useState<any>("");
  const [reserveHours, setReserveHours] = useState<any>("");
  const [reserveDays, setReserveDays] = useState<any>("");
  const [reserveNotes, setReserveNotes] = useState("");
  const [description, setDescription] = useState("");
  // EOL — quartz calibres only (UI gated on movement type)
  const [eol, setEol] = useState(false);
  const [eolNotes, setEolNotes] = useState("");
  const [certificationIds, setCertificationIds] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // complications
  const availableCompRef = useRef<any[]>([]);
  const checkedCompRef = useRef<any[]>([]);
  const compNotesRef = useRef<any[]>([]);

  useEffect(() => {
    availableCompRef.current = [...calibreComplicationList];
    forceUpdate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calibreComplicationList]);

  const availableComp = availableCompRef.current;
  const checkedComp = checkedCompRef.current;
  const compNotes = compNotesRef.current;

  function handleChangeHz(e: any) {
    const val = e.target.value;
    setHz(val);
    setVph(val !== "" ? Math.round(val * 7200) : "");
    setHzError(val !== "" && val % 0.5 !== 0);
  }

  function handleChangeVph(e: any) {
    const val = e.target.value;
    setVph(val);
    setHz(val !== "" ? val / 7200 : "");
    setHzError(val !== "" && val % 7200 !== 0);
  }

  function handleChangeHours(e: any) {
    const val = e.target.value;
    setReserveHours(val);
    setReserveDays(val !== "" && Number(val) > 0 ? parseFloat((Number(val) / 24).toFixed(2)) : "");
  }

  function handleChangeDays(e: any) {
    const val = e.target.value;
    setReserveDays(val);
    setReserveHours(val !== "" && Number(val) > 0 ? Math.round(Number(val) * 24) : "");
  }

  const certListFiltered = certificationList.filter(
    (c: any) => c.brand_id === selectedBrandId || c.brand_id === null,
  );

  const canSubmit = calibreName.trim().length > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitError("");
    setIsSubmitting(true);
    try {
      const { data: newCalibre, error } = await authPost(
        `/details-value?field=calibre&site=0`,
        { name: calibreName, brand_id: selectedBrandId ?? undefined },
      );
      if (error || !newCalibre?.calibre_id) {
        setSubmitError(
          error?.message ||
            "Could not add this calibre. It may already exist, or the server rejected the request.",
        );
        setIsSubmitting(false);
        return;
      }
      const newId = newCalibre.calibre_id;
      await dispatch(
        WatchActions.editMovement(`/calibre-details/${newId}`, {
          in_house: inHouse,
          movement_type_id: movementTypeId || null,
          brand_id: selectedBrandId ?? null,
          calibre: calibreName,
          certification_ids: certificationIds,
          parent_calibre_id: baseCalibreId || null,
          description,
          eol: eol ? 1 : 0,
          eol_notes: eolNotes || null,
          reserve_days: Number(reserveDays) || 0,
          reserve_hours: Number(reserveHours) || 0,
          reserve_notes: reserveNotes,
          vph: Number(vph) || 0,
          components: components !== "" ? Number(components) : null,
          complications: checkedComp.map((c: any) => c.complication_id),
          complication_details: Object.values(
            compNotes.reduce((acc: any, note: any) => {
              const id = note.calibre_complication_id;
              if (!acc[id]) acc[id] = { calibre_id: newId, calibre_complication_id: id, details: [] };
              if (note.details?.trim()) acc[id].details.push(note.details.trim());
              return acc;
            }, {})
          ).map((n: any) => ({ ...n, details: n.details.join("\n") || null })),
          jewels: jewels ? Number(jewels) : 0,
        }),
      );
      // Reset form
      setCalibreName("");
      setSelectedBrandId(null);
      setBaseCalibreId("");
      setMovementTypeId("");
      setInHouse(false);
      setHz("");
      setVph("");
      setJewels("");
      setComponents("");
      setReserveHours("");
      setReserveDays("");
      setReserveNotes("");
      setDescription("");
      setEol(false);
      setEolNotes("");
      setCertificationIds([]);
      checkedCompRef.current = [];
      availableCompRef.current = [...calibreComplicationList];
      compNotesRef.current = [];
      if (onSuccess) onSuccess();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (listsLoading) return <Box p={2} style={{ textAlign: "center", color: "rgba(0,0,0,0.4)", fontSize: 12 }}>Loading...</Box>;

  return (
    <Box
      display="flex"
      flexDirection="column"
      my={2}
      p={2}
      style={{
        border: "1px solid rgba(0,0,0,0.1)",
        borderRadius: 4,
        boxShadow: "0px 1px 3px rgba(0,0,0,0.06)",
        gap: 8,
      }}
    >
      {/* Row 1: Calibre name + Brand */}
      <Box display="flex" style={{ gap: 12 }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Calibre</label>
          <RecentValuesInput
            rememberKey="calibre_name"
            style={inputStyle}
            value={calibreName}
            onChange={(e) => setCalibreName(e.target.value)}
            placeholder="Calibre name"
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Brand Affiliation</label>
          <select
            style={selectStyle}
            value={selectedBrandId ?? ""}
            onChange={(e) => setSelectedBrandId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value=""></option>
            {brandListTitle.map((b: any) => (
              <option key={b.id} value={b.id}>{b.title}</option>
            ))}
          </select>
        </div>
      </Box>

      {/* Row 2: In House + Movement Type + Certification */}
      <Box display="flex" alignItems="flex-end" style={{ gap: 12 }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <label style={labelStyle}>In House</label>
          <Checkbox
            color="primary"
            checked={inHouse}
            onClick={() => setInHouse(!inHouse)}
            style={{ padding: 0 }}
          />
        </div>
        <div style={{ width: 200 }}>
          <label style={labelStyle}>Movement Type</label>
          <select
            style={selectStyle}
            value={movementTypeId}
            onChange={(e) => setMovementTypeId(e.target.value ? Number(e.target.value) : "")}
          >
            <option value=""></option>
            {movementTypeList.map((m: any) => (
              <option key={m.movement_id} value={m.movement_id}>{m.movement}</option>
            ))}
          </select>
        </div>
        {!!movementTypeId && movementTypeList.some((m: any) => m.movement_id === movementTypeId && (m.movement || "").toLowerCase() === "quartz") && (
          <>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <label style={labelStyle}>EOL</label>
              <Checkbox
                color="primary"
                checked={eol}
                onClick={() => setEol(!eol)}
                style={{ padding: 0 }}
              />
            </div>
            <div style={{ width: 140 }}>
              <label style={labelStyle}>EOL Notes</label>
              <RecentValuesInput
                rememberKey="calibre_eol_notes"
                style={selectStyle}
                type="text"
                value={eolNotes}
                onChange={(e) => setEolNotes(e.target.value)}
              />
            </div>
          </>
        )}
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Certification</label>
          <MultiSelectChips
            value={certificationIds}
            onChange={setCertificationIds}
            options={certListFiltered}
            getOptionValue={(c: any) => c.certification_id}
            getOptionLabel={(c: any) => c.certification}
            style={{ width: "100%", border: "1px solid rgba(0,0,0,0.23)" }}
          />
        </div>
      </Box>

      {/* Row 3: Base Calibre + HZ + VPH + Jewels + Components + Hours + Days */}
      <Box display="flex" alignItems="flex-end" style={{ gap: 12, flexWrap: "wrap" }}>
        <div style={{ width: 200 }}>
          <label style={labelStyle}>Base Calibre</label>
          <select
            style={selectStyle}
            value={baseCalibreId}
            onChange={(e) => setBaseCalibreId(e.target.value ? Number(e.target.value) : "")}
          >
            <option value=""></option>
            {calibreList.map((c: any) => (
              <option key={c.calibre_id} value={c.calibre_id}>{c.calibre}</option>
            ))}
          </select>
        </div>
        <div style={{ width: 60 }}>
          <label style={{ ...labelStyle, color: hzError ? "red" : labelStyle.color }}>
            {hzError ? "Invalid HZ:" : "HZ"}
          </label>
          <RecentValuesInput
            rememberKey="calibre_hz"
            style={{ ...inputStyle, borderColor: hzError ? "red" : undefined }}
            type="number"
            value={hz}
            onChange={handleChangeHz}
          />
        </div>
        <div style={{ width: 70 }}>
          <label style={labelStyle}>VPH</label>
          <RecentValuesInput rememberKey="calibre_vph" style={inputStyle} type="number" value={vph} onChange={handleChangeVph} />
        </div>
        <div style={{ width: 60 }}>
          <label style={labelStyle}>Jewels</label>
          <RecentValuesInput rememberKey="calibre_jewels" style={inputStyle} type="number" value={jewels} onChange={(e) => setJewels(e.target.value)} />
        </div>
        <div style={{ width: 80 }}>
          <label style={labelStyle}>Components</label>
          <RecentValuesInput rememberKey="calibre_components" style={inputStyle} type="number" value={components} onChange={(e) => setComponents(e.target.value)} />
        </div>
        <div style={{ width: 70 }}>
          <label style={labelStyle}>Hours</label>
          <RecentValuesInput rememberKey="calibre_reserve_hours" style={inputStyle} type="number" value={reserveHours} onChange={handleChangeHours} />
        </div>
        <div style={{ width: 70 }}>
          <label style={labelStyle}>Days</label>
          <RecentValuesInput rememberKey="calibre_reserve_days" style={inputStyle} type="number" value={reserveDays} onChange={handleChangeDays} />
        </div>
      </Box>

      {/* Row 4: Power Reserve Notes */}
      <div>
        <label style={labelStyle}>Power Reserve Notes</label>
        <RecentValuesInput
          rememberKey="calibre_reserve_notes"
          style={{ ...inputStyle, width: "100%" }}
          value={reserveNotes}
          onChange={(e) => setReserveNotes(e.target.value)}
        />
      </div>

      {/* Row 5: Movement Notes */}
      <div>
        <label style={labelStyle}>Movement Notes</label>
        <ReactQuill
          theme="snow"
          value={description}
          onChange={(content) => setDescription(content)}
        />
      </div>

      {/* Complications */}
      {calibreComplicationList.length > 0 && (
        <FormControl component="fieldset" style={{ width: "100%" }}>
          <FormLabel component="legend" style={{ fontSize: 11 }}>Select Complications</FormLabel>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "2px 0", marginTop: 4 }}>
            {availableComp.map((comp: any, index: number) => {
              const listId = comp.complication_id ?? comp.calibre_complication_id;
              const listName = comp.complication ?? comp.calibre_complication;
              return (
                <FormControlLabel
                  key={listId}
                  control={
                    <Checkbox
                      color="primary"
                      size="medium"
                      checked={false}
                      onClick={() => {
                        availableComp.splice(index, 1);
                        checkedComp.push({ complication_id: listId, complication: listName });
                        forceUpdate();
                      }}
                    />
                  }
                  label={listName}
                  labelPlacement="end"
                />
              );
            })}
          </div>
        </FormControl>
      )}

      <Box>
        {checkedComp.map((value: any, index: number) => (
          <Box key={value.complication_id ?? index}>
            <Box display="flex">
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={true}
                    onClick={() => {
                      availableComp.push(value);
                      checkedComp.splice(index, 1);
                      forceUpdate();
                    }}
                  />
                }
                label={value.complication}
                labelPlacement="end"
              />
              <Tooltip title="Add Details Field">
                <Button
                  onClick={() => {
                    compNotes.push({ calibre_id: null, calibre_complication_id: value.complication_id, details: "" });
                    forceUpdate();
                  }}
                >
                  <AddCircleOutlineIcon />
                </Button>
              </Tooltip>
            </Box>
            {compNotes.map((note: any, noteIndex: number) => {
              if (note.calibre_complication_id !== value.complication_id) return null;
              return (
                <Box key={noteIndex} ml={5} pt={1} display="flex" alignItems="center">
                  <RecentValuesInput
                    rememberKey="calibre_complication_details"
                    wrapperStyle={{ flex: 1 }}
                    style={inputStyle}
                    value={note.details}
                    onChange={(e) => { compNotes[noteIndex].details = e.target.value; forceUpdate(); }}
                  />
                  <Tooltip title="Remove">
                    <Button onClick={() => { compNotes.splice(noteIndex, 1); forceUpdate(); }} style={{ minWidth: 0, padding: 4 }}>
                      <RemoveCircleOutlineOutlinedIcon color="secondary" style={{ fontSize: 18 }} />
                    </Button>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      {/* Submit */}
      <Box mt={1}>
        {isSubmitting && <LinearProgress style={{ marginBottom: 8 }} />}
        {submitError && (
          <Box mb={1} style={{ color: "#d32f2f", fontSize: 12 }}>
            {submitError}
          </Box>
        )}
        <Button
          variant="contained"
          color="primary"
          disabled={!canSubmit || isSubmitting}
          onClick={handleSubmit}
        >
          Add Calibre
        </Button>
      </Box>
    </Box>
  );
}

export const AddWatchAttribute = (props: Props) => {
  const [myValue, setValue] = useState("");
  const [myBrand, setBrand] = useState("");
  const [myBrandId, setMyBrandId] = useState(Number);
  const [caseBackCategory, setCaseBackCategory] = useState("");
  const [disabled, setDisabled] = useState(true);
  const [brandDisabled, setBrandDisabled] = useState(true);
  const [bgColor, setBgColor] = useState("");
  const siteId = useSelector((state: any) => state.scopes.site_id);
  const { brand: brandList } = useSelector(WatchSelectors.watches, shallowEqual);

  const brandListTitle = brandList?.map((brands: any) => {
    return { title: brands.brand, id: brands.brand_id };
  }) || [];
  const caseCategories = [
    { title: "Diameter", value: "Diameter" },
    { title: "Width & Height", value: "Width+Height" },
  ];

  if (props.rawAttribute === "calibre" && siteId === 0) {
    return (
      <div style={{ width: "100%" }}>
        <AddCalibreForm onSuccess={props.onSuccess} />
      </div>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      {siteId === 0 && (
        <Box
          display="flex"
          justifyContent="center"
          my={2}
          p={2}
          pt={2}
          bgcolor={bgColor}
          style={{
            border: "1px solid rgba(0,0,0,0.1)",
            borderRadius: 4,
            boxShadow: "0px 1px 3px rgba(0,0,0,0.06)",
          }}
        >
          <Box p={1}>
            <TextField
              value={myValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                let myValue: string = e.target.value;
                setValue(myValue);
                setDisabled(false);
                setBgColor("background.paper");
              }}
              label={props.attribute}
            />
          </Box>
          <Box p={1}>
            {props.brandAffiliated && (
              <Autocomplete
                id="combo-box-demo"
                options={brandListTitle}
                getOptionLabel={(option: any) => option.title}
                onChange={(_event, value) => {
                  if (value) {
                    setBrand(value.title);
                    setMyBrandId(value.id);
                    setBrandDisabled(false);
                  }
                }}
                style={{ width: 235 }}
                renderInput={(params) => (
                  <TextField {...params} label="Select a Brand Affiliation" />
                )}
              />
            )}
            {props.isCaseShape && (
              <Autocomplete
                id="case-shape-category"
                options={caseCategories}
                getOptionLabel={(option: any) => option.title}
                onChange={(_event, value: any) => {
                  setCaseBackCategory(value?.value ?? value?.title);
                }}
                style={{ width: 235 }}
                renderInput={(params) => (
                  <TextField {...params} label="Select a Category" />
                )}
              />
            )}
          </Box>
          <Box p={1}>
            <ConfirmButton
              attribute={props.attribute}
              myValue={myValue}
              myBrand={myBrand}
              myBrandId={myBrandId}
              caseBackCategory={caseBackCategory}
              rawAttribute={props.rawAttribute}
              Add_Edit_Delete={"add"}
              color={"primary"}
              disabled={props.rawAttribute === "series" ? brandDisabled : disabled}
              path={props.path}
              onSuccess={props.onSuccess}
            ></ConfirmButton>
          </Box>
        </Box>
      )}
    </div>
  );
};